import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import { CreateShareRequest, CreateShareResponse, UpdateShareRequest, ShareResponse } from 'src/types/TShare';
import { createShare, updateShare, deleteShare, deleteShareComment } from '../functions/shareApi';
import { queryNames } from '../utils/queryNames';

/**
 * Mutation hook for creating a new share
 */
export function useCreateShareMutation(
  mutationOptions?: UseMutationOptions<CreateShareResponse, FetchError, CreateShareRequest>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = async (data: CreateShareRequest) => {
    const result = await createShare(accessToken, data);
    if ('error' in result) {
      throw result;
    }
    return result as CreateShareResponse;
  };

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      // Invalidate shares list to refetch
      queryClient.invalidateQueries([queryNames.share, 'list']);
      mutationOptions?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Mutation hook for updating an existing share
 */
export function useUpdateShareMutation(
  shareId: string,
  mutationOptions?: UseMutationOptions<ShareResponse, FetchError, UpdateShareRequest>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = async (data: UpdateShareRequest) => {
    const result = await updateShare(accessToken, shareId, data);
    if ('error' in result) {
      throw result;
    }
    return result as ShareResponse;
  };

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      // Invalidate both list and individual share queries
      queryClient.invalidateQueries([queryNames.share, 'list']);
      queryClient.invalidateQueries([queryNames.share, shareId]);
      mutationOptions?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Retire a review that will never come back.
 *
 * Separate from `useUpdateShareMutation` because that one binds its shareId
 * when the hook is created, which a list of rows cannot do — the id is only
 * known at click time.
 */
export function useCloseReviewMutation(
  mutationOptions?: UseMutationOptions<ShareResponse, FetchError, string>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = async (shareId: string) => {
    const result = await updateShare(accessToken, shareId, { close: true });
    if ('error' in result) {
      throw result;
    }
    return result as ShareResponse;
  };

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: (data, shareId, context) => {
      queryClient.invalidateQueries([queryNames.share, 'list']);
      queryClient.invalidateQueries([queryNames.share, shareId]);
      mutationOptions?.onSuccess?.(data, shareId, context);
    },
  });
}

/**
 * Mutation hook for deleting a share
 */
export function useDeleteShareMutation(
  mutationOptions?: UseMutationOptions<any, FetchError, string>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = async (shareId: string) => {
    const result = await deleteShare(accessToken, shareId);
    if ('error' in result) {
      throw result;
    }
    return result;
  };

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      // Invalidate shares list to refetch
      queryClient.invalidateQueries([queryNames.share, 'list']);
      mutationOptions?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Mutation hook for adding assets to an existing share
 */
export function useAddAssetsToShareMutation(
  shareId: string,
  mutationOptions?: UseMutationOptions<ShareResponse, FetchError, string[]>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = async (fileIds: string[]) => {
    const result = await updateShare(accessToken, shareId, { file_ids: fileIds });
    if ('error' in result) {
      throw result;
    }
    return result as ShareResponse;
  };

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      // Invalidate both list and individual share queries
      queryClient.invalidateQueries([queryNames.share, 'list']);
      queryClient.invalidateQueries([queryNames.share, shareId]);
      mutationOptions?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Mutation hook for deleting a comment from a share
 */
export function useDeleteCommentMutation(
  shareId: string,
  mutationOptions?: UseMutationOptions<any, FetchError, number>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = async (commentId: number) => {
    const result = await deleteShareComment(accessToken, shareId, commentId);
    if ('error' in result) {
      throw result;
    }
    return result;
  };

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      // Invalidate comments query
      queryClient.invalidateQueries([queryNames.share, shareId, 'comments']);
      mutationOptions?.onSuccess?.(data, variables, context);
    },
  });
}

